import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { AllocationViewProfile } from './../allocation-view-profiles';
import {
  AllocationViewClearHighlightedUli,
  AllocationViewClearLastChangedUli,
  AllocationViewInitialState,
  AllocationViewSelectSavedViewMode,
  AllocationViewSetCalendarEdit,
  AllocationViewSetFixedTimelineValue,
  AllocationViewSetHighlightedSlot,
  AllocationViewSetHighlightedUli,
  AllocationViewSetLastChangedUli,
  AllocationViewSetLastChangedUliAsHighlighted,
  AllocationViewSetLastSelectedUli,
  AllocationViewSetLastSelectedUliToChanged,
  AllocationViewSetProfile,
  AllocationViewSetViewMode,
  AllocationViewTriggerRefresh,
} from './allocation-view.actions';
import { AllocationViewService } from './allocation-view.service';
import { AllocationViewFixedTimelineModel, AllocationViewMode } from './constants';

export interface AllocationViewStateModel {
  selectedViewMode: AllocationViewMode;
  lastSelectedUli: string;
  lastChangedUli: string;
  highlightedUli: string;
  highlightedSlot: number;
  selectedProfile: AllocationViewProfile;
  listViewTimeline: boolean;
  calendarViewTimeline: boolean;
  triggerRefresh: boolean;
  isTimelineFixed: boolean;
  isCalendarEdit: boolean;
}

const initialState: AllocationViewStateModel = {
  selectedViewMode: AllocationViewMode.List,
  lastSelectedUli: null,
  lastChangedUli: null,
  highlightedUli: null,
  highlightedSlot: null,
  selectedProfile: null,
  listViewTimeline: true,
  calendarViewTimeline: true,
  triggerRefresh: true,
  isTimelineFixed: true,
  isCalendarEdit: false,
};

@State<AllocationViewStateModel>({
  name: 'allocationView',
  defaults: initialState,
})
@Injectable()
export class AllocationViewState {
  @Selector()
  static getSelectedViewMode(state: AllocationViewStateModel): AllocationViewMode {
    return state.selectedViewMode;
  }

  @Selector()
  static getHighlightedUli(state: AllocationViewStateModel): string {
    return state.highlightedUli;
  }

  @Selector()
  static getHighlightedSlot(state: AllocationViewStateModel): number {
    return state.highlightedSlot;
  }

  @Selector()
  static getLastChangedUli(state: AllocationViewStateModel): string {
    return state.lastChangedUli;
  }

  @Selector()
  static getLastSelectedUli(state: AllocationViewStateModel): string {
    return state.lastSelectedUli;
  }

  @Selector()
  static getFixedTimeline(state: AllocationViewStateModel): boolean {
    return state.isTimelineFixed;
  }

  @Selector()
  static getSelectedProfile(state: AllocationViewStateModel): AllocationViewProfile {
    return state.selectedProfile;
  }

  @Selector()
  static getCalendarViewTimeline(state: AllocationViewStateModel): boolean {
    return state.calendarViewTimeline;
  }

  @Selector()
  static getListViewTimeline(state: AllocationViewStateModel): boolean {
    return state.listViewTimeline;
  }

  @Selector()
  static getRefreshTrigger(state: AllocationViewStateModel): boolean {
    return state.triggerRefresh;
  }

  @Selector()
  static getIsCalendarEdit(state: AllocationViewStateModel): boolean {
    return state.isCalendarEdit;
  }

  constructor(private allocationViewService: AllocationViewService) {}

  @Action(AllocationViewSelectSavedViewMode)
  selectSavedViewMode(ctx: StateContext<AllocationViewStateModel>): void {
    const savedViewMode: AllocationViewMode = this.allocationViewService.getSavedViewMode();

    if (savedViewMode) {
      ctx.patchState({
        selectedViewMode: savedViewMode,
      });
    }
  }

  @Action(AllocationViewSetViewMode)
  setViewMode(ctx: StateContext<AllocationViewStateModel>, { viewMode }: AllocationViewSetViewMode): void {
    this.allocationViewService.saveViewMode(viewMode);

    ctx.patchState({
      selectedViewMode: viewMode,
    });
  }

  @Action(AllocationViewSetLastChangedUli)
  setLastChangedUli(ctx: StateContext<AllocationViewStateModel>, { uli }: AllocationViewSetLastChangedUli): void {
    ctx.dispatch(new AllocationViewClearLastChangedUli()).subscribe(() => {
      ctx.patchState({
        lastChangedUli: uli,
      });
    });
  }

  @Action(AllocationViewSetLastSelectedUliToChanged)
  setLastSelectedUliAsChanged(ctx: StateContext<AllocationViewStateModel>): void {
    ctx.dispatch(new AllocationViewSetLastChangedUli(ctx.getState().lastSelectedUli));
  }

  @Action(AllocationViewSetLastSelectedUli)
  setLastSelectedUli(ctx: StateContext<AllocationViewStateModel>, { uli }: AllocationViewSetLastSelectedUli): void {
    ctx.patchState({
      lastSelectedUli: uli,
    });
  }

  @Action(AllocationViewSetProfile)
  setSelectedProfile(ctx: StateContext<AllocationViewStateModel>, { profile }: AllocationViewSetProfile): void {
    ctx.patchState({
      selectedProfile: profile,
    });
  }

  @Action(AllocationViewClearLastChangedUli)
  clearLastChangedUli(ctx: StateContext<AllocationViewStateModel>): void {
    ctx.patchState({
      lastChangedUli: null,
    });
  }

  @Action(AllocationViewSetLastChangedUliAsHighlighted)
  setLastChangedUliAsHighlighted(ctx: StateContext<AllocationViewStateModel>): void {
    const { lastChangedUli } = ctx.getState();

    if (!lastChangedUli) {
      return;
    }
    ctx.dispatch(new AllocationViewSetHighlightedUli(lastChangedUli));
  }

  @Action(AllocationViewSetHighlightedUli)
  setHighlightedUli(ctx: StateContext<AllocationViewStateModel>, { uli }: AllocationViewSetHighlightedUli): void {
    ctx.dispatch(new AllocationViewClearHighlightedUli()).subscribe(() => {
      ctx.patchState({
        highlightedUli: uli,
      });
    });
  }

  @Action(AllocationViewClearHighlightedUli)
  clearHighlightedUli(ctx: StateContext<AllocationViewStateModel>): void {
    ctx.patchState({
      highlightedUli: null,
    });
  }

  @Action(AllocationViewSetHighlightedSlot)
  setHighlightedSlot(
    ctx: StateContext<AllocationViewStateModel>,
    { payload: { slotId, dialog } }: AllocationViewSetHighlightedSlot,
  ): void {
    ctx.dispatch(new AllocationViewClearHighlightedUli()).subscribe(() => {
      if (dialog) {
        dialog.close();
      }
      ctx.patchState({
        highlightedSlot: slotId,
      });
    });
  }

  @Action(AllocationViewTriggerRefresh)
  triggerRefresh(ctx: StateContext<AllocationViewStateModel>, payload: boolean): void {
    ctx.patchState({
      triggerRefresh: payload,
    });
  }

  @Action(AllocationViewSetFixedTimelineValue)
  setFixedTimeline(
    ctx: StateContext<AllocationViewStateModel>,
    { payload }: AllocationViewSetFixedTimelineValue,
  ): void {
    const { selectedViewMode } = ctx.getState();
    const savedTimelineConfig: AllocationViewFixedTimelineModel =
      this.allocationViewService.getFixedTimelineFromStorage();
    const updatedTimelineConfig: Partial<AllocationViewFixedTimelineModel> = {};

    if (typeof payload === 'boolean') {
      updatedTimelineConfig[selectedViewMode] = payload;
    }

    const value: AllocationViewFixedTimelineModel = {
      [selectedViewMode]: true,
      ...savedTimelineConfig,
      ...updatedTimelineConfig,
    };

    this.allocationViewService.saveFixedTimelineToStorage(value);

    ctx.patchState({
      isTimelineFixed: value[selectedViewMode],
    });
  }

  @Action(AllocationViewSetCalendarEdit)
  setCalendarEdit(ctx: StateContext<AllocationViewStateModel>, { payload }: AllocationViewSetCalendarEdit): void {
    ctx.patchState({
      isCalendarEdit: payload,
    });
  }

  @Action(AllocationViewInitialState)
  setInitialState(ctx: StateContext<AllocationViewInitialState>): void {
    ctx.setState(initialState);
  }
}
